import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, CircularProgress, TextField } from "@material-ui/core";

import { createAvatar, getStockFaces } from "../../Redux/Actions/agent";
import { selectStockFaces } from "../../Redux/Selectors";
import { StockFace } from "../../Redux/Sagas/agent/api";

// "Choose a stock presenter": grid of Tavus stock faces with a hover/tap
// preview, plus a small input to add an existing Tavus face ID.
const StockFacePicker: React.FC = () => {
  const dispatch = useDispatch();
  const faces: StockFace[] = useSelector(selectStockFaces);
  const loading: boolean = useSelector((state: any) => state.agent.stockFacesLoading);
  const error: string | null = useSelector((state: any) => state.agent.stockFacesError);
  const creating: boolean = useSelector((state: any) => state.agent.creatingAvatar);

  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [faceIdInput, setFaceIdInput] = useState("");

  const selected = faces.find((face) => face.faceId === selectedId) || null;

  const addSelected = () => {
    if (!selected) return;
    dispatch(
      createAvatar({ source: "stock", faceId: selected.faceId, name: name.trim() || selected.name })
    );
    setSelectedId(null);
    setName("");
  };

  const addById = () => {
    const faceId = faceIdInput.trim();
    if (!faceId) return;
    dispatch(createAvatar({ source: "stock", faceId }));
    setFaceIdInput("");
  };

  const renderFace = (face: StockFace) => {
    const isSelected = face.faceId === selectedId;
    const showVideo = Boolean(face.previewVideoUrl) && (isSelected || hoveredId === face.faceId);
    return (
      <button
        type="button"
        key={face.faceId}
        className={`avatarFace ${isSelected ? "avatarFaceSelected" : ""}`}
        onClick={() => setSelectedId(isSelected ? null : face.faceId)}
        onMouseEnter={() => setHoveredId(face.faceId)}
        onMouseLeave={() => setHoveredId(null)}
        aria-pressed={isSelected}
      >
        {showVideo ? (
          <video
            className="avatarFaceMedia"
            src={face.previewVideoUrl || undefined}
            poster={face.thumbnailUrl || undefined}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : face.thumbnailUrl ? (
          <img className="avatarFaceMedia" src={face.thumbnailUrl} alt={face.name} loading="lazy" />
        ) : (
          <span className="avatarFaceMedia" />
        )}
        <span className="avatarFaceName">{face.name}</span>
      </button>
    );
  };

  return (
    <div className="aiCard">
      <div className="aiCardTitle">Choose a stock presenter</div>
      <div className="aiCardHint">
        Ready to use right away. Hover or tap a presenter to preview, then add it.
      </div>

      {loading && faces.length === 0 ? (
        <div className="aiLoading">
          <CircularProgress size={28} />
        </div>
      ) : error && faces.length === 0 ? (
        <div className="aiNotice aiNotice--error">
          Could not load stock presenters: {error}{" "}
          <Button size="small" onClick={() => dispatch(getStockFaces())}>
            Try again
          </Button>
        </div>
      ) : faces.length === 0 ? (
        <div className="aiMuted">No stock presenters are available right now.</div>
      ) : (
        <div className="avatarFaces">{faces.map(renderFace)}</div>
      )}

      {selected && (
        <div className="aiRow">
          <TextField
            label="Name (optional)"
            placeholder={selected.name}
            value={name}
            onChange={(event) => setName(event.target.value)}
            variant="outlined"
            size="small"
            className="aiField"
          />
          <div>
            <Button
              variant="contained"
              className="aiPrimaryButton"
              onClick={addSelected}
              disabled={creating}
            >
              {creating ? "Adding..." : `Add ${selected.name}`}
            </Button>
          </div>
        </div>
      )}

      <div className="inboxSection">
        <div className="aiCardHint">Use an existing Tavus face ID: paste a face ID from your Tavus account.</div>
        <div className="aiRow">
          <TextField
            label="Tavus face ID"
            value={faceIdInput}
            onChange={(event) => setFaceIdInput(event.target.value)}
            variant="outlined"
            size="small"
            className="aiField"
          />
          <div>
            <Button variant="outlined" onClick={addById} disabled={creating || !faceIdInput.trim()}>
              Add face
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockFacePicker;
